import { segment } from '@actions/analytics';
import Image from '@base/FutureImage';
import { COURSE_CARD_CLICKED } from '@constants/segment';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { LOGIN_STARTED } from '@cont/Login/constants';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { getLink } from '@models/Item';
import { useToggleable } from '@stores/toggleables';
import { Spacings } from '@styles/index';
import { compareDates, getDateShort, getTime, isSameDay } from '@utils/dates';
import { nFormatter } from '@utils/textHelper';
import Divider from 'aquilla/core/atoms/Divider';
import EducatorThumbnailBackground from 'aquilla/core/atoms/EducatorThumbnailBackground';
import Tag from 'aquilla/core/atoms/Tag';
import Typography from 'aquilla/core/atoms/Typography';
import IconButton from 'aquilla/core/molecules/IconButton';
// import getTextColor from 'aquilla/core/shared/utils/getTextColor';
import Play from 'aquilla/icons/actions/Play';
import Link from 'next/link';

const LiveClassTag = styled(Tag)`
  text-transform: uppercase;
  padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_2B};
`;

const PulseAnimation = (width) => keyframes`
  50% {
    box-shadow: 0 0 0 ${width} rgba(235, 87, 87, 0.125);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(235, 87, 87, 0);
  }
`;

const LiveIconButton = styled(IconButton)`
  position: absolute;
  bottom: ${Spacings.SPACING_4B};
  right: ${Spacings.SPACING_4B};
  width: ${Spacings.SPACING_18B};
  aspect-ratio: 1;
  border-radius: 50%;
  padding: ${Spacings.SPACING_4B};
  box-shadow: 0 0 0 0 rgba(235, 87, 87, 0.5);
  animation: ${() => PulseAnimation(Spacings.SPACING_2B)} 2s infinite;
  transition: 0.5s;

  &:hover,
  &:focus,
  &:active {
    transform: scale(1.1);
  }

  svg {
    color: var(--color-i-white);
  }
`;

const CompletedPlayIconButton = styled(IconButton)`
  position: absolute;
  bottom: ${Spacings.SPACING_4B};
  right: ${Spacings.SPACING_4B};
  width: ${Spacings.SPACING_18B};
  aspect-ratio: 1;
  border-radius: 50%;
  padding: ${Spacings.SPACING_4B};
  background: ${(props) => `${props.$buttonColor}33`};
  border-color: transparent;
  transition: 0.5s;

  &:hover,
  &:active,
  &:focus {
    transform: scale(1.1);
    background: ${(props) => `${props.$buttonColor}33`};
    border-color: transparent;
  }

  svg {
    color: ${(props) => props.$buttonColor};
  }
`;

const ClassCardThumbnailMainInfoLeft = styled.div`
  position: absolute;
  bottom: ${Spacings.SPACING_4B};
  left: ${Spacings.SPACING_4B};
  display: flex;
  gap: ${Spacings.SPACING_2B};
  justify-content: flex-start;
  flex: 1;
`;
const StyledDivider = styled(Divider)`
  width: ${Spacings.SPACING_6B};
  border-width: 0px 0px ${Spacings.SPACING_1B};
  margin: ${Spacings.SPACING_6B} 0;
`;

const ClassContainer = styled.div`
  width: var(--layout-width);
  display: flex;
  height: 316px;
`;

const ThumbnailWrapper = styled.div`
  height: 100%;
  flex: 0 0 561px;
  position: relative;
  cursor: pointer;
  border-radius: ${Spacings.SPACING_2B};
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  height: 100%;
  /* flex-basis: calc(100% - 561px); */
  padding: ${Spacings.SPACING_4B};
`;

const ClassTopicAndLangInfo = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${(props) => props.theme.measurements.spacing(4)};
  align-items: center;
`;

const ClassName = styled.div`
  margin: ${Spacings.SPACING_4B} 0 ${Spacings.SPACING_2B};
  cursor: pointer;
`;
const ClassTopicName = styled(Typography)`
  color: ${(props) => props.$topicColor || props.theme.palette.VIOLET};
  font-size: ${(props) => props.theme.measurements.spacing(6)};
  font-weight: 700;
  line-height: ${(props) => props.theme.measurements.spacing(6)};
  text-transform: uppercase;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const EducatorName = styled.div`
  margin-bottom: ${Spacings.SPACING_2B};
  cursor: pointer;
`;

const ClassCard = ({ classInfo }) => {
  const today = new Date();
  let timeSchedule;
  const viewCount =
    classInfo.totalViews || classInfo.nextSession.properties?.attendance;

  const startRelative = compareDates(classInfo.startsAt, today); // 1 if already started
  const endRelative = compareDates(classInfo.endsAt, today); // 1 if already ended
  const isLive = startRelative > 0 && endRelative < 0;
  const isUpcoming = startRelative < 0;
  const isCompleted = endRelative > 0;
  const isStartDayToday = isSameDay(new Date(classInfo.startsAt), today);
  const isEndDayToday = isSameDay(new Date(classInfo.endsAt), today);
  const goalSegmentData = useGoalEventProps({});
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const login = () => {
    segment.track(LOGIN_STARTED);
    openLoginDrawer();
  };
  const handleOnClick = (e) => {
    e.preventDefault();
    segment.track(COURSE_CARD_CLICKED, {
      ...goalSegmentData,
      last_primary_source_section: 'Carousel Classes',
      last_primary_source: 'Goal Home'
    });
  };
  if (isUpcoming) {
    if (isStartDayToday)
      timeSchedule = `Starts at ${getTime(classInfo.startsAt)}`;
    else timeSchedule = `Starts on ${getDateShort(classInfo.startsAt)}`;
  } else if (isCompleted) {
    if (isEndDayToday) timeSchedule = `Ended at ${getTime(classInfo.endsAt)}`;
    else timeSchedule = `Ended on ${getDateShort(classInfo.endsAt)}`;
  } else if (isLive) {
    if (isEndDayToday) timeSchedule = `Ends at ${getTime(classInfo.endsAt)}`;
    else timeSchedule = `Ends on ${getDateShort(classInfo.endsAt)}`;
  }

  let viewerStat;
  if ((isLive || classInfo.isLive) && viewCount)
    viewerStat = `${nFormatter(viewCount, 1)} learners are watching`;
  else if (isCompleted && viewCount)
    viewerStat = `${nFormatter(viewCount, 1)} learners watched`;
  const classLink = getLink({
    ...classInfo,
    nextTrack: {
      ...classInfo.nextSession,
      ...classInfo.nextSession.properties
    }
  });

  return (
    <ClassContainer>
      <ThumbnailWrapper onClick={login}>
        {classInfo.coverPhotoV1 ? (
          <Image
            src={classInfo.coverPhotoV1}
            alt=""
            sizes="380px"
            height={316}
            width={561}
          />
        ) : (
          <EducatorThumbnailBackground
            variant="content-thumbnail-with-person"
            color={classInfo.topicColor}
            width={561}
            height={316}
          />
        )}
        {classInfo.isLive ? (
          <LiveIconButton variant="danger" size="large" onClick={handleOnClick}>
            <Play height={22} width={22} />
          </LiveIconButton>
        ) : (
          <CompletedPlayIconButton
            size="large"
            $buttonColor={classInfo.topicColor}
          >
            <Play height={22} width={22} />
          </CompletedPlayIconButton>
        )}
        <ClassCardThumbnailMainInfoLeft>
          {classInfo.isLive && (
            <LiveClassTag
              variant="solid"
              color="var(--color-i-red)"
              size="small"
            >
              Live
            </LiveClassTag>
          )}
        </ClassCardThumbnailMainInfoLeft>
      </ThumbnailWrapper>
      <ContentWrapper>
        <ClassTopicAndLangInfo>
          <Tag variant="default" size="small">
            {classInfo.languageDisplay}
          </Tag>
          <ClassTopicName variant="p2" $topicColor={classInfo.topicColor}>
            {classInfo.topicName}
          </ClassTopicName>
        </ClassTopicAndLangInfo>
        <Link href={classLink} passHref>
          <ClassName>
            <Typography variant="h3">{classInfo.name}</Typography>
          </ClassName>
        </Link>
        <Link href={classInfo.educatorLink} passHref>
          <EducatorName>
            <Typography variant="p2" color="var(--color-text-primary)">
              {classInfo.educatorName}
            </Typography>
          </EducatorName>
        </Link>
        <Typography variant="p2" color="var(--color-text-secondary)">
          {timeSchedule}
        </Typography>
        <StyledDivider />
        <Typography variant="p1" color="var(--color-text-primary)">
          {viewerStat}
        </Typography>
      </ContentWrapper>
    </ClassContainer>
  );
};

export default ClassCard;
